import {
  IAction,
  IActionDefinitionDto,
  IComponentDefinition,
  IDisplayItem,
  IEvent,
  IQuickChildrenRef,
  IQuickCommandRef,
  IQuickEventRef,
  PlaceToType
} from "@kuika/kuika-cl-model";
import React from "react";
import { ScreenDesignHelper } from "../../../../../shared/utility/screen-design-helper";
import {
  IQuickOption,
  QuickAddRow,
  QuickChip,
  QuickOptionList,
  QuickPopover,
  QuickTip,
  useQuickOpenState
} from "./quick-controls";
import { toDisplayLabel, toMidSentence } from "./quick-labels";
import { IQuickContext, openEventInPanel, safeTestIdPart } from "./quick-shared";

// ── event ──────────────────────────────────────────────────────────────────────────────────────

/** The actions attached to one event of the component. */
export function getEventActions(di: IDisplayItem, eventName: string): IAction[] {
  return di.item.events?.find((e: IEvent): boolean => e.eventName === eventName)?.actions ?? [];
}

/** An action's title as the event panel shows it: its definition's display name, else its description. */
export function getActionTitle(action: IAction, definitions: IActionDefinitionDto[] | undefined): string {
  const def: IActionDefinitionDto | undefined = definitions?.find(
    (d: IActionDefinitionDto): boolean => d.id === action.actionDefinitionId
  );
  return def?.displayName ?? def?.name ?? action.description ?? action.label;
}

export interface IQuickEventControlProps {
  ctx: IQuickContext;
  entry: IQuickEventRef;
}

export const QuickEventControl = ({ ctx, entry }: IQuickEventControlProps): JSX.Element => {
  const [isOpen, setIsOpen] = useQuickOpenState();
  const label: string = toDisplayLabel(entry.name);
  const actions: IAction[] = getEventActions(ctx.displayItem, entry.name);
  const testId: string = `quickProp-event-${entry.name}`;
  const open = (mode: "add" | "edit"): void => {
    setIsOpen(false);
    openEventInPanel(ctx, entry.name, mode);
  };
  return (
    <QuickPopover
      open={isOpen}
      onOpenChange={setIsOpen}
      testId={`quickPropPopover-event-${entry.name}`}
      content={
        <>
          <div className="kuika-quick-pop__title">{label}</div>
          <div className="kuika-quick-pop__list" role="list" aria-label={`${label} actions`}>
            {actions.map((action: IAction, index: number): JSX.Element => (
              <div
                key={action.id ?? index}
                role="listitem"
                className="kuika-quick-pop__item"
                data-testid={`quickPropEventAction-${entry.name}-${index}`}
              >
                <span className="kuika-quick-pop__item-sub">{index + 1}</span>
                <span className="kuika-quick-pop__item-label">{getActionTitle(action, ctx.sd.actionDefinitions)}</span>
              </div>
            ))}
            {actions.length === 0 && <div className="kuika-quick-pop__empty">No actions yet.</div>}
          </div>
          <div className="kuika-quick-pop__footer">
            <button
              type="button"
              className="kuika-quick-pop__footer-button kuika-quick-pop__footer-button--primary"
              data-testid={`quickPropEventAdd-${entry.name}`}
              onClick={(): void => open("add")}
            >
              Add action
            </button>
            <button
              type="button"
              className="kuika-quick-pop__footer-button"
              data-testid={`quickPropEventEdit-${entry.name}`}
              onClick={(): void => open("edit")}
            >
              Edit in panel
            </button>
          </div>
        </>
      }
    >
      <QuickTip label={label} isSuppressed={isOpen}>
        <QuickChip
          testId={testId}
          ariaLabel={`${label}: ${actions.length} action${actions.length === 1 ? "" : "s"}`}
          icon="bolt"
          value={label}
          count={actions.length}
          isWarning={entry.warnWhenEmpty === true && actions.length === 0}
          hasPopup
          isOpen={isOpen}
          onClick={(): void => setIsOpen(!isOpen)}
        />
      </QuickTip>
    </QuickPopover>
  );
};

// ── children / addChild ─────────────────────────────────────────────────────────────────────────

/** Every descendant (any depth) whose component is one of `childNames`, in tree order. */
export function findDescendants(di: IDisplayItem, childNames: string[]): IDisplayItem[] {
  const out: IDisplayItem[] = [];
  const walk = (node: IDisplayItem): void => {
    node.children?.forEach((child: IDisplayItem): void => {
      if (child.item?.name !== undefined && childNames.includes(child.item.name)) {
        out.push(child);
      }
      walk(child);
    });
  };
  walk(di);
  return out;
}

/**
 * Where a `childName` component is added: the component itself when its definition lists it as a child,
 * else the first descendant whose definition does (a Table's columns live under its TableRow).
 */
export function findAddContainer(
  di: IDisplayItem,
  childName: string,
  definitionOf: (node: IDisplayItem) => IComponentDefinition | undefined
): IDisplayItem | undefined {
  const accepts = (node: IDisplayItem): boolean => definitionOf(node)?.childrenNames?.includes(childName) === true;
  const queue: IDisplayItem[] = [di];
  while (queue.length > 0) {
    const node: IDisplayItem = queue.shift() as IDisplayItem;
    if (accepts(node)) {
      return node;
    }
    queue.push(...(node.children ?? []));
  }
  return undefined;
}

const addChild = (ctx: IQuickContext, childName: string): void => {
  const container: IDisplayItem | undefined = findAddContainer(
    ctx.displayItem,
    childName,
    (node: IDisplayItem): IComponentDefinition | undefined =>
      ctx.sd.project?.componentSetObj?.components?.find((c: IComponentDefinition): boolean => c.id === node.item?.id) ??
      ctx.sd.getComponentDefinition(node.item)
  );
  if (container) {
    ctx.sd.addChildItemByComponentName(container.id.toString(), childName, PlaceToType.PlaceToLastChildItem);
  }
};

export interface IQuickCommandControlProps {
  ctx: IQuickContext;
  entry: IQuickCommandRef;
}

export const QuickCommandControl = ({ ctx, entry }: IQuickCommandControlProps): JSX.Element => {
  const label: string = toDisplayLabel(entry.label ?? entry.childName);
  const addLabel: string = `Add ${toMidSentence(label)}`;
  return (
    <QuickTip label={addLabel}>
      <QuickChip
        testId={`quickProp-add-${entry.childName}`}
        ariaLabel={addLabel}
        glyph="+"
        value={label}
        onClick={(): void => addChild(ctx, entry.childName)}
      />
    </QuickTip>
  );
};

export interface IQuickChildrenControlProps {
  ctx: IQuickContext;
  entry: IQuickChildrenRef;
}

export const QuickChildrenControl = ({ ctx, entry }: IQuickChildrenControlProps): JSX.Element => {
  const [isOpen, setIsOpen] = useQuickOpenState();
  const children: IDisplayItem[] = findDescendants(ctx.displayItem, entry.childNames);
  const labelPart: string = safeTestIdPart(entry.label);
  const label: string = toDisplayLabel(entry.label);
  const highlight = (id: string | null): void => ctx.sd.highlightHoveredItem(id ?? "");
  return (
    <QuickPopover
      open={isOpen}
      onOpenChange={(open: boolean): void => {
        if (!open) {
          highlight(null);
        }
        setIsOpen(open);
      }}
      testId={`quickPropPopover-children-${labelPart}`}
      content={
        <QuickOptionList
          title={label}
          options={children.map((child: IDisplayItem): IQuickOption => ({
            value: child.id.toString(),
            label: ScreenDesignHelper.getFriendlyName(child.id.toString(), ctx.sd.screenDesignDictionary),
            sub: child.item.name ? toDisplayLabel(child.item.name) : undefined
          }))}
          current={null}
          emptyText={`No ${toMidSentence(label)} yet`}
          optionTestId={(o: IQuickOption): string => `quickPropChild-${labelPart}-${o.value}`}
          onHover={highlight}
          onPick={(id: string): void => {
            highlight(null);
            setIsOpen(false);
            ctx.sd.setSelectedDisplayItem(id);
          }}
          footer={
            entry.addChild ? (
              <QuickAddRow
                label={`Add ${toMidSentence(toDisplayLabel(entry.addChild))}`}
                testId={`quickPropChildrenAdd-${labelPart}`}
                onClick={(): void => {
                  setIsOpen(false);
                  addChild(ctx, entry.addChild as string);
                }}
              />
            ) : undefined
          }
        />
      }
    >
      <QuickTip label={label} isSuppressed={isOpen}>
        <QuickChip
          testId={`quickProp-children-${labelPart}`}
          ariaLabel={`${label}: ${children.length}`}
          value={label}
          count={children.length}
          hasPopup
          isOpen={isOpen}
          onClick={(): void => setIsOpen(!isOpen)}
        />
      </QuickTip>
    </QuickPopover>
  );
};
